// Client-side mirror of the member reservation guardrails.
//
// AUTHORITY LIVES IN functions/memberBookings.js. This copy exists so the
// booking dialog can warn before a round-trip; it decides nothing. CRA can't
// import from functions/, so the two must be changed together — the tests in
// memberGuards.test.js pin the behaviour both are expected to share.

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "HH:MM" -> minutes since midnight */
export function toMins(t) {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + (m || 0);
}

/**
 * Minutes of [startTime, endTime) on `date` overlapping a prime block.
 * Boundaries exclusive: a booking ending exactly at prime start doesn't touch.
 * Independent of weekend bucketing — prime is whatever rentalTimeBlocks says.
 */
export function primeMinutes(startTime, endTime, date, primeBlocks) {
  const startMins = toMins(startTime);
  const endMins = toMins(endTime);
  if (!(endMins > startMins)) return 0;

  const dayAbbr = DAY_ABBR[new Date(`${date}T12:00:00`).getDay()];
  const intervals = (primeBlocks || [])
    .filter(pb => Array.isArray(pb.days) && pb.days.includes(dayAbbr))
    .map(pb => [Math.max(startMins, toMins(pb.startTime)), Math.min(endMins, toMins(pb.endTime))])
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);

  let total = 0, curStart = null, curEnd = null;
  for (const [s, e] of intervals) {
    if (curStart === null) { curStart = s; curEnd = e; continue; }
    if (s <= curEnd) { curEnd = Math.max(curEnd, e); }
    else { total += curEnd - curStart; curStart = s; curEnd = e; }
  }
  if (curStart !== null) total += curEnd - curStart;
  return total;
}

/** True when any minute of the range overlaps a prime block. */
export function touchesPrime(startTime, endTime, date, primeBlocks) {
  return primeMinutes(startTime, endTime, date, primeBlocks) > 0;
}

/**
 * Guardrail 0 — is member court booking live yet?
 * settings/membershipTiers.reservationsOpenDate, blank = open. Compared against
 * TODAY in club time: it gates the feature, not which dates are bookable.
 */
export function reservationsOpen(openDate, todayStr) {
  if (!openDate) return true;
  return String(todayStr) >= String(openDate);
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** "2026-08-31" -> "August 31, 2026", for member-facing copy. */
export function formatOpenDate(d) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d || ''));
  if (!m) return String(d || '');
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

/** Tiers flagged courtReservationsBlocked cannot reserve courts at all. */
export function checkCanReserve(membership, tier) {
  const status = String(membership?.status || '');
  if (!['active', 'trialing'].includes(status)) {
    return { ok: false, code: 'membership_not_active',
      message: 'Your membership must be active to reserve a court.' };
  }
  if (tier?.courtReservationsBlocked) {
    return { ok: false, code: 'reservations_not_included',
      message: 'Your membership does not include court reservations.' };
  }
  return { ok: true };
}

/** Guardrail 1 — prime requires a tier without primeBlocked, in good standing. */
export function checkPrimeEligibility(membership, tier) {
  const base = checkCanReserve(membership, tier);
  if (!base.ok) return base;
  if (tier?.primeBlocked) {
    return { ok: false, code: 'prime_not_included',
      message: 'Prime-time reservations are not included in your membership. '
        + 'You can book any time outside prime.' };
  }
  return { ok: true };
}

/**
 * Guardrail 4 — may this member switch the court camera on for a reservation?
 *
 * OPT-IN, unlike primeBlocked and courtReservationsBlocked next to it. Those
 * are opt-OUTs because court time is what a membership is for: a new tier
 * should be able to book unless an admin says otherwise. Streaming is the
 * other way round — it is a paid perk of particular tiers, so a tier that has
 * never heard of it must not quietly acquire it the day the feature ships, and
 * a tier created next season must not either. Absent flag = no streaming.
 *
 * Deliberately NOT a reason to hide the switch. Every member sees it; the ones
 * whose tier does not carry it see it disabled with this message, which is how
 * anybody learns the perk exists.
 */
export function checkCanStream(membership, tier) {
  const base = checkCanReserve(membership, tier);
  if (!base.ok) return base;
  if (!tier?.streamingIncluded) {
    return { ok: false, code: 'streaming_not_included',
      message: 'Live streaming is not included in your membership. '
        + 'Upgrade to stream and record your reservations.' };
  }
  return { ok: true };
}

/** Guardrail 2 — one prime-touching booking per member per club-local day. */
export function checkOnePrimePerDay(existing, localDate) {
  const clash = (existing || []).find(b =>
    b.touchesPrime === true && b.localDate === localDate && b.status !== 'cancelled');
  if (!clash) return { ok: true };
  const courts = Array.isArray(clash.courts) ? clash.courts.join(', ') : (clash.courts || '');
  const courtLabel = String(courts).replace(/court-/g, '');
  return {
    ok: false, code: 'prime_one_per_day',
    message: `You're limited to one prime-time reservation per day. `
      + `You already have a prime reservation on ${clash.localDate} at `
      + `${clash.startTime}–${clash.endTime}${courtLabel ? ` (Court ${courtLabel})` : ''}.`,
  };
}


// ── Guardrail 3 — the club's reservation limits (settings/rentalPolicy) ─────
//
// Mirror of checkGuardrails in functions/memberBookings.js, which decides. The
// four caps here are the "Reservation Guardrails" panel on Court Rentals →
// Policy; the tier "Daily Caps & Booking Policy" block is a different thing and
// still observation-only behind policyEnforcementEnabled.

// MUST match DEFAULT_POLICY in functions/memberBookings.js and defaultPolicy in
// src/pages/CourtRentals.js — the numbers an admin sees when no rentalPolicy
// doc is saved.
const DEFAULT_POLICY = {
  maxBookingDurationHours: 2.5,
  maxAdvanceDays: 14,
  minAdvanceHours: 1,
  defaultMaxCourts: 2,
};

/** 0 means unlimited; missing or nonsense falls back to the default, not to "off". */
function policyCap(policy, key) {
  const raw = (policy || {})[key];
  // null / undefined / '' mean "not set", NOT "unlimited". Number(null) is 0 and
  // 0 is how this app spells unlimited, so coercing first would turn a blank
  // field into no cap at all — the exact failure this guard exists to close.
  if (raw === null || raw === undefined || raw === '') return DEFAULT_POLICY[key];
  const v = Number(raw);
  return Number.isFinite(v) && v >= 0 ? v : DEFAULT_POLICY[key];
}

/** "3 hours", "1 hour", "1.5 hours" — the field steps in halves. */
function fmtHours(n) {
  return `${Number(n.toFixed(2))} hour${n === 1 ? '' : 's'}`;
}

function toLocalNoon(dateStr) { return new Date(`${dateStr}T12:00:00`); }

/**
 * Pass hoursUntilStart from dates.js hoursUntil(); NaN or null skips the
 * lead-time check rather than guessing, since guessing refuses valid bookings.
 */
export function checkGuardrails(policy, p) {
  const { date, startTime, endTime, courts, todayStr, hoursUntilStart, tierMaxDurationHours } = p || {};

  // A tier may set its own longest reservation (Gold: 3.5 h) over the club's.
  // Blank falls back to the club's, never to "no limit" — the same rule as
  // policyCap — and 0 is unlimited, the app-wide convention.
  const tierCap = (tierMaxDurationHours === null || tierMaxDurationHours === undefined
    || tierMaxDurationHours === '') ? NaN : Number(tierMaxDurationHours);
  const maxDuration = Number.isFinite(tierCap) && tierCap >= 0
    ? tierCap : policyCap(policy, 'maxBookingDurationHours');
  const durationHours = (toMins(endTime) - toMins(startTime)) / 60;
  if (maxDuration > 0 && durationHours > maxDuration) {
    return { ok: false, code: 'duration_exceeded',
      message: `Reservations can be at most ${fmtHours(maxDuration)} long.` };
  }

  const maxCourts = policyCap(policy, 'defaultMaxCourts');
  const courtCount = (courts || []).length;
  if (maxCourts > 0 && courtCount > maxCourts) {
    return { ok: false, code: 'court_limit_exceeded',
      message: `You can book at most ${maxCourts} court${maxCourts === 1 ? '' : 's'} at a time.` };
  }

  const minAdvance = policyCap(policy, 'minAdvanceHours');
  if (minAdvance > 0 && Number.isFinite(hoursUntilStart) && hoursUntilStart < minAdvance) {
    return { ok: false, code: 'min_advance',
      message: `Reservations must be made at least ${fmtHours(minAdvance)} before they start.` };
  }

  const maxDays = policyCap(policy, 'maxAdvanceDays');
  if (maxDays > 0 && date && todayStr) {
    const daysAhead = Math.round((toLocalNoon(date) - toLocalNoon(todayStr)) / 86400000);
    if (daysAhead > maxDays) {
      return { ok: false, code: 'advance_window_exceeded',
        message: `Reservations can be made at most ${maxDays} day${maxDays === 1 ? '' : 's'} ahead.` };
    }
  }

  return { ok: true };
}
